<!-- src/components/modal/provident-modal.vue -->
<script setup>
import { ref, onMounted, watch, nextTick, computed, onBeforeUnmount } from "vue";
import Choices from "choices.js";
import { useProvidentStore } from "@/stores/providentStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import * as bootstrap from "bootstrap";

const store = useProvidentStore();
const employees = useEmployeeStore();
const emit = defineEmits(["refresh"]);

const mode = ref("create");
const saving = ref(false);
const attempted = ref(false);

const form = ref({
  id: null,
  employee_id: "",
  fund_type: "Employee",
  employee_share_percent: "",
  organization_share_percent: "",
  employee_share_amount: "",
  organization_share_amount: "",
  status: "Approved",
  description: "",
});

/* Modal ref + instance */
const modalEl = ref(null);
let modalInstance = null;

/* Employee select (Choices) */
const employeeSelectRef = ref(null);
let employeeChoices = null;

const fundTypeLabel = computed(() =>
  form.value.fund_type === "Voluntary"
    ? "Voluntary Provident Fund"
    : "Employee Provident Fund"
);

const statusPill = computed(() => {
  const s = String(form.value.status || "").toLowerCase();
  if (s.includes("approve")) return { cls: "dq-pill-success", text: "Approved" };
  if (s.includes("pending")) return { cls: "dq-pill-warn", text: "Pending" };
  return { cls: "dq-pill-muted", text: form.value.status || "Status" };
});

/* Validation */
function isEmpty(v) {
  return v === null || v === undefined || String(v).trim() === "";
}
function isNumericOrEmpty(v) {
  if (isEmpty(v)) return true;
  return !Number.isNaN(Number(v));
}
const errors = computed(() => {
  const e = {};
  if (isEmpty(form.value.employee_id)) e.employee_id = "Employee is required.";
  if (!isNumericOrEmpty(form.value.employee_share_percent)) e.employee_share_percent = "Enter a valid number.";
  if (!isNumericOrEmpty(form.value.organization_share_percent)) e.organization_share_percent = "Enter a valid number.";
  if (!isNumericOrEmpty(form.value.employee_share_amount)) e.employee_share_amount = "Enter a valid number.";
  if (!isNumericOrEmpty(form.value.organization_share_amount)) e.organization_share_amount = "Enter a valid number.";
  return e;
});
const isValid = computed(() => Object.keys(errors.value).length === 0);

/* ---------- Bootstrap modal helpers ---------- */
function showModal() {
  if (!modalInstance && modalEl.value) {
    modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl.value, {
      backdrop: "static",
      keyboard: true,
      focus: true,
    });

    modalEl.value.addEventListener("shown.bs.modal", () => {
      nextTick(() => {
        // Focus Choices search input if possible
        const search = modalEl.value?.querySelector(".choices__input--cloned");
        search?.focus?.();
      });
    });

    modalEl.value.addEventListener("hidden.bs.modal", () => {
      attempted.value = false;
      // optional: keep data for edit? we reset on open anyway
    });
  }

  modalInstance?.show();
}

function hideModal() {
  modalInstance?.hide();
}

/* ---------- Choices init ---------- */
function destroyChoices() {
  if (employeeChoices) {
    employeeChoices.destroy();
    employeeChoices = null;
  }
}

function initEmployeeSelect(selectedId = null) {
  nextTick(() => {
    if (!employeeSelectRef.value) return;

    destroyChoices();

    employeeChoices = new Choices(employeeSelectRef.value, {
      searchEnabled: true,
      itemSelectText: "",
      placeholder: true,
      allowHTML: true,
      shouldSort: false,
      searchResultLimit: 20,
    });

    if (selectedId) {
      employeeChoices.setChoiceByValue(String(selectedId));
    }
  });
}

/* When employee list changes */
watch(
  () => employees.employees,
  () => initEmployeeSelect(form.value.employee_id || null),
  { deep: true }
);

/* ---------- Open create/edit ---------- */
function resetForm() {
  attempted.value = false;
  form.value = {
    id: null,
    employee_id: "",
    fund_type: "Employee",
    employee_share_percent: "",
    organization_share_percent: "",
    employee_share_amount: "",
    organization_share_amount: "",
    status: "Approved",
    description: "",
  };
}

function openCreate() {
  mode.value = "create";
  resetForm();
  showModal();
  initEmployeeSelect(null);
}

function openEdit(row) {
  mode.value = "edit";

  form.value = {
    id: row.id,
    employee_id: row.employee_id,
    fund_type: row.fund_type || "Employee",
    employee_share_percent: row.employee_share_percent ?? "",
    organization_share_percent: row.organization_share_percent ?? "",
    employee_share_amount: row.employee_share_amount ?? "",
    organization_share_amount: row.organization_share_amount ?? "",
    status: row.status || "Approved",
    description: row.description || "",
  };

  showModal();
  initEmployeeSelect(form.value.employee_id);
}

/* ---------- Submit ---------- */
async function submitForm() {
  attempted.value = true;
  if (!isValid.value) return;

  saving.value = true;
  try {
    if (mode.value === "create") {
      await store.createItem(form.value);
    } else {
      await store.updateItem(form.value.id, form.value);
    }
    hideModal();
    emit("refresh");
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  employees.fetchEmployees?.();
});

onBeforeUnmount(() => {
  destroyChoices();
  if (modalEl.value) {
    const inst = bootstrap.Modal.getInstance(modalEl.value);
    inst?.dispose?.();
  }
});

defineExpose({ openCreate, openEdit });
</script>

<template>
  <div class="modal fade dq-modal" id="provident_modal" tabindex="-1" ref="modalEl" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge" :class="mode === 'edit' ? 'dq-icon-badge-warn' : ''">
              <i :class="mode === 'edit' ? 'ti ti-edit' : 'ti ti-coin'"></i>
            </span>

            <div class="lh-1">
              <h4 class="modal-title mb-1">
                {{ mode === "create" ? "Add Provident Fund" : "Edit Provident Fund" }}
              </h4>
              <div class="dq-subtitle">
                Configure employee & organization contributions with optional percent/amount fields.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="saving || store.loadingForCreate"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitForm" novalidate>
          <div class="modal-body dq-modal-body">
            <div class="dq-form-grid">
              <!-- Employee + Fund type -->
              <div class="dq-two">
                <div class="dq-field">
                  <label class="form-label dq-label">
                    Employee <span class="text-danger">*</span>
                  </label>

                  <!-- Choices.js select -->
                  <select
                    ref="employeeSelectRef"
                    v-model="form.employee_id"
                    class="form-select dq-select"
                    :aria-invalid="attempted && !!errors.employee_id"
                  >
                    <option value="">Select Employee</option>
                    <option v-for="e in employees.employees" :key="e.id" :value="e.id">
                      {{ e.first_name }} {{ e.last_name }}
                    </option>
                  </select>

                  <div v-if="attempted && errors.employee_id" class="dq-error">
                    <i class="ti ti-alert-circle me-1"></i>{{ errors.employee_id }}
                  </div>
                  <div v-else class="dq-help">Search by name and pick one employee.</div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Fund Type</label>

                  <div class="dq-select-wrap">
                    <span class="dq-input-icon"><i class="ti ti-category"></i></span>
                    <select class="form-select dq-select" v-model="form.fund_type">
                      <option value="Employee">Employee Provident Fund</option>
                      <option value="Voluntary">Voluntary Provident Fund</option>
                    </select>
                  </div>

                  <div class="dq-help">
                    <span class="dq-pill dq-pill-info">{{ fundTypeLabel }}</span>
                  </div>
                </div>
              </div>

              <!-- Percent rows -->
              <div class="dq-card">
                <div class="dq-card-head">
                  <div>
                    <div class="dq-card-title">Contribution Percentages</div>
                    <div class="dq-card-sub">Optional — use if you calculate contributions by salary.</div>
                  </div>
                </div>

                <div class="dq-two">
                  <div class="dq-field">
                    <label class="form-label dq-label">Employee Share (%)</label>
                    <div class="dq-input-wrap">
                      <span class="dq-input-icon"><i class="ti ti-percentage"></i></span>
                      <input class="form-control dq-input dq-input-right" v-model="form.employee_share_percent" placeholder="e.g. 5" />
                    </div>
                    <div v-if="attempted && errors.employee_share_percent" class="dq-error">
                      <i class="ti ti-alert-circle me-1"></i>{{ errors.employee_share_percent }}
                    </div>
                  </div>

                  <div class="dq-field">
                    <label class="form-label dq-label">Organization Share (%)</label>
                    <div class="dq-input-wrap">
                      <span class="dq-input-icon"><i class="ti ti-percentage"></i></span>
                      <input class="form-control dq-input dq-input-right" v-model="form.organization_share_percent" placeholder="e.g. 5" />
                    </div>
                    <div v-if="attempted && errors.organization_share_percent" class="dq-error">
                      <i class="ti ti-alert-circle me-1"></i>{{ errors.organization_share_percent }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Amount rows -->
              <div class="dq-card">
                <div class="dq-card-head">
                  <div>
                    <div class="dq-card-title">Fixed Amounts</div>
                    <div class="dq-card-sub">Optional — use if you collect a fixed contribution amount.</div>
                  </div>
                </div>

                <div class="dq-two">
                  <div class="dq-field">
                    <label class="form-label dq-label">Employee Amount</label>
                    <div class="dq-input-wrap">
                      <span class="dq-input-icon"><i class="ti ti-currency-dollar"></i></span>
                      <input class="form-control dq-input dq-input-right" v-model="form.employee_share_amount" placeholder="0.00" />
                    </div>
                    <div v-if="attempted && errors.employee_share_amount" class="dq-error">
                      <i class="ti ti-alert-circle me-1"></i>{{ errors.employee_share_amount }}
                    </div>
                  </div>

                  <div class="dq-field">
                    <label class="form-label dq-label">Organization Amount</label>
                    <div class="dq-input-wrap">
                      <span class="dq-input-icon"><i class="ti ti-currency-dollar"></i></span>
                      <input class="form-control dq-input dq-input-right" v-model="form.organization_share_amount" placeholder="0.00" />
                    </div>
                    <div v-if="attempted && errors.organization_share_amount" class="dq-error">
                      <i class="ti ti-alert-circle me-1"></i>{{ errors.organization_share_amount }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status + Description -->
              <div class="dq-two">
                <div class="dq-field">
                  <label class="form-label dq-label">Status</label>
                  <div class="dq-select-wrap">
                    <span class="dq-input-icon"><i class="ti ti-toggle-left"></i></span>
                    <select class="form-select dq-select" v-model="form.status">
                      <option value="Approved">Approved</option>
                      <option value="Pending">Pending</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div class="dq-help">
                    <span class="dq-pill" :class="statusPill.cls">{{ statusPill.text }}</span>
                    <span class="ms-2 text-muted">Controls whether it’s used in payroll calculations.</span>
                  </div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Description</label>
                  <textarea class="form-control dq-textarea" rows="3" v-model="form.description" placeholder="Optional notes…"></textarea>
                  <div class="dq-help">Optional. Helps HR understand the setup.</div>
                </div>
              </div>

              <!-- Tip -->
              <div class="dq-tip">
                <div class="dq-tip-icon"><i class="ti ti-bulb"></i></div>
                <div>
                  <div class="dq-tip-title">Quick tip</div>
                  <div class="dq-tip-text">
                    Use either percentages or fixed amounts (or both) depending on your payroll policy.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button type="button" class="btn btn-light dq-btn-soft me-2" data-bs-dismiss="modal" :disabled="saving">
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="saving || store.loadingForCreate">
              <span v-if="saving || store.loadingForCreate" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="saving || store.loadingForCreate">
                {{ mode === "create" ? "Adding…" : "Saving…" }}
              </span>
              <span v-else>
                {{ mode === "create" ? "Add Provident Fund" : "Save Changes" }}
              </span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== DQ Premium Modal System ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 18px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  font-size: 20px;
}
.dq-icon-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-modal-body {
  padding: 18px;
  max-height: 76vh;
  overflow: auto;
}

.dq-form-grid {
  display: grid;
  gap: 14px;
}

.dq-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
@media (max-width: 991px) {
  .dq-two {
    grid-template-columns: 1fr;
  }
}

.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap,
.dq-select-wrap {
  position: relative;
}

.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}

.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input-right {
  text-align: right;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

.dq-select {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
}
.dq-select:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-textarea:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

.dq-error {
  margin-top: 6px;
  font-size: 0.82rem;
  font-weight: 800;
  color: rgba(220, 38, 38, 1);
  display: inline-flex;
  align-items: center;
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.96);
  padding: 12px;
}
.dq-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.dq-card-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}
.dq-card-sub {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.55);
  margin-top: 2px;
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.75rem;
}
.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-pill-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}
.dq-pill-muted {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(71, 85, 105, 1);
}
.dq-pill-info {
  background: rgba(2, 132, 199, 0.10);
  color: rgba(3, 105, 161, 1);
}

/* Tip card */
.dq-tip {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
}
.dq-tip-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.14);
  color: rgba(79, 70, 229, 1);
}
.dq-tip-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-tip-text {
  font-size: 0.83rem;
  color: rgba(17, 24, 39, 0.6);
}

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}
.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}
.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* ===== Choices.js Skin to match DQ ===== */
:deep(.choices) {
  margin-bottom: 0;
}
:deep(.choices__inner) {
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  padding: 6px 10px 6px 12px;
  box-shadow: none;
}
:deep(.choices__input--cloned) {
  margin: 0;
  padding: 0;
}
:deep(.choices__list--dropdown),
:deep(.choices__list[aria-expanded]) {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.16);
  overflow: hidden;
}
:deep(.choices__item--choice) {
  padding: 10px 12px;
}
:deep(.choices__item--choice.is-highlighted) {
  background: rgba(59, 130, 246, 0.10);
}
</style>
